// WhatsApp share helpers for admin outreach.
// Uses the "click-to-chat" wa.me URL — no API integration required.
//
// Iter 102 · Manual-only messaging while the Gupshup background API is
// temporarily disabled. Every "Send reminder" / "WhatsApp" affordance in
// the admin UI routes through the helpers below and opens WhatsApp Web
// (or the native app) with the message pre-composed for the admin. The
// server-side Gupshup + Twilio endpoints stay intact and are bypassed by
// the UI — flip `gupshup_service._MANUAL_ONLY_DEFAULT` (or export
// `GUPSHUP_MANUAL_ONLY=false`) to re-enable background sends later.

// wa.me needs digits with country code, no '+'.
// The app stores 10-digit national numbers; prepend 91 (India) at link-build
// time. If the caller passes a longer number assume it's already qualified.
export const normalizeWhatsAppPhone = (phone) => {
    const digits = String(phone || "").replace(/\D+/g, "");
    return digits.length === 10 ? `91${digits}` : digits;
};

export const buildProfileLink = (token) => {
    if (typeof window === "undefined") return `/form/${token}`;
    return `${window.location.origin}/form/${token}`;
};

/**
 * Governor-signed directory reminder template. Blank governor/district
 * segments collapse gracefully so callers that don't yet supply the
 * context still produce a readable message.
 *
 * Final format (per user spec):
 *   "Rotary Greetings from <Governor Name>, Governor RI District <District Number>.
 *    Dear <Member Name>, please verify and complete your profile for the upcoming
 *    official directory. Click here to view/edit: <Login Link>"
 */
export const buildDirectoryReminderMessage = ({
    governorName,
    districtNumber,
    memberName,
    token,
} = {}) => {
    const gov = String(governorName || "").trim();
    const dn = String(districtNumber || "").trim();
    const name = String(memberName || "").trim() || "there";
    const link = buildProfileLink(token);
    let greeting;
    if (gov && dn) greeting = `Rotary Greetings from ${gov}, Governor RI District ${dn}.`;
    else if (gov) greeting = `Rotary Greetings from ${gov}.`;
    else if (dn) greeting = `Rotary Greetings from Governor RI District ${dn}.`;
    else greeting = `Rotary Greetings.`;
    return `${greeting} Dear ${name}, please verify and complete your profile for the upcoming official directory. Click here to view/edit: ${link}`;
};

export const buildDirectoryReminderUrl = ({
    governorName,
    districtNumber,
    memberName,
    phone,
    token,
} = {}) => {
    const digits = normalizeWhatsAppPhone(phone);
    const text = encodeURIComponent(buildDirectoryReminderMessage({
        governorName, districtNumber, memberName, token,
    }));
    if (!digits) return `https://wa.me/?text=${text}`;
    return `https://wa.me/${digits}?text=${text}`;
};

// Legacy positional signature kept so callers that don't yet pass the
// governor context (e.g. AdminMemberProfile, AssignRoleDialog) keep
// working. Both delegate to the governor-signed template so the message
// body stays consistent across every outreach surface.
export const buildWhatsAppMessage = (name, token /* , progress unused */) =>
    buildDirectoryReminderMessage({ memberName: name, token });

export const buildWhatsAppUrl = (name, phone, token /* , progress unused */) =>
    buildDirectoryReminderUrl({ memberName: name, phone, token });
